import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import {
  applyStorePricingFromVariantResource,
  fetchCartAttributes,
  loadStoreCatalog,
  refreshStorePricing,
  STORE_LOCATOR_SOURCE,
  STORE_SELECTED_EVENT,
} from '@theme/store-pricing';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number} */
  #pricingRequestId = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.variantUpdate, this.#handleDocumentVariantUpdate, { signal });

    if (this.dataset.storePricingEnabled === 'true') {
      document.addEventListener(STORE_SELECTED_EVENT, this.#handleStorePricingRefresh, { signal });
      document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdateForStorePricing, { signal });
      void this.#refreshStorePricing();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = undefined;
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #handleDocumentVariantUpdate = (event) => {
    if (!this.isConnected) return;

    const eventProductId = String(event.detail.data.productId ?? '');
    const myProductId = String(this.dataset.productId ?? '');
    const fromMyPicker =
      event.target instanceof Element &&
      this.closest('.product-information, dialog')?.contains(event.target) &&
      String(event.target.closest('variant-picker')?.dataset.productId ?? '') === myProductId;

    if (eventProductId && eventProductId !== myProductId && !fromMyPicker) return;

    void this.updatePrice(event);
  };

  /**
   * @param {Document | Element} html
   * @returns {HTMLElement | null}
   */
  #findProductPriceInHtml(html) {
    const blockId = this.dataset.blockId;
    if (!blockId) return null;

    const match = html.querySelector(`product-price[data-block-id="${blockId}"]`);
    return match instanceof HTMLElement ? match : null;
  }

  /**
   * @param {string} [requestUrl] - Variant-picker fetch URL (includes current option_values)
   * @returns {Promise<HTMLElement | null>}
   */
  async #fetchProductPriceFromSection(requestUrl) {
    const sectionId = this.dataset.sectionId?.trim();
    if (!sectionId) return null;

    /** @type {string | undefined} */
    let url;

    if (requestUrl?.trim()) {
      try {
        const parsed = new URL(requestUrl, window.location.origin);
        parsed.searchParams.set('section_id', sectionId);
        url = parsed.toString();
      } catch {
        return null;
      }
    } else {
      const variantPicker = this.closest('variant-picker');
      const productUrl = variantPicker instanceof HTMLElement ? variantPicker.dataset.productUrl?.trim() : undefined;
      if (!productUrl) return null;

      const search = window.location.search.replace(/^\?/, '');
      url = search
        ? `${productUrl}?section_id=${encodeURIComponent(sectionId)}&${search}`
        : `${productUrl}?section_id=${encodeURIComponent(sectionId)}`;
    }

    try {
      const response = await fetch(url);
      if (!response.ok) return null;

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      return this.#findProductPriceInHtml(doc);
    } catch {
      return null;
    }
  }

  /**
   * @param {HTMLElement} newProductPrice
   */
  #morphFromProductPriceElement(newProductPrice) {
    const priceContainer = this.querySelector('[ref="priceContainer"]');
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');

    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    const volumePricingNote = this.querySelector('[ref="volumePricingNote"]');
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      const livePriceContainer = this.querySelector('[ref="priceContainer"]');
      livePriceContainer?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    if (this.dataset.storePricingEnabled === 'true') {
      const newVariantScript = newProductPrice.querySelector('script[data-store-pricing-variants]');
      const currentVariantScript = this.querySelector('script[data-store-pricing-variants]');
      if (newVariantScript && currentVariantScript) {
        currentVariantScript.textContent = newVariantScript.textContent;
      }
    }
  }

  /**
   * @returns {number}
   */
  #nextPricingRequestId() {
    this.#pricingRequestId += 1;
    return this.#pricingRequestId;
  }

  #refreshStorePricing(expectedVariantId) {
    const variantId = expectedVariantId ?? this.dataset.currentVariantId?.trim();
    if (!variantId) return;

    const requestId = this.#nextPricingRequestId();
    void refreshStorePricing(this, { requestId, expectedVariantId: variantId });
  }

  #handleStorePricingRefresh = () => {
    this.#refreshStorePricing();
  };

  /** @param {CustomEvent} event */
  #handleCartUpdateForStorePricing = (event) => {
    if (event.detail?.data?.source !== STORE_LOCATOR_SOURCE) return;
    this.#refreshStorePricing();
  };

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    const resource = event.detail.resource;
    let newProductPrice = event.detail.data.html ? this.#findProductPriceInHtml(event.detail.data.html) : null;

    const variantId = resource?.id ?? newProductPrice?.dataset.currentVariantId;
    if (variantId) {
      this.dataset.currentVariantId = String(variantId);
    }

    if (!newProductPrice) {
      newProductPrice = await this.#fetchProductPriceFromSection(event.detail.data.requestUrl);
    }

    if (newProductPrice) {
      this.#morphFromProductPriceElement(newProductPrice);
    }

    const installmentsInput = /** @type {HTMLInputElement|null} */ (
      this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
    );
    if (installmentsInput && resource?.id) {
      installmentsInput.value = String(resource.id);
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (this.dataset.storePricingEnabled !== 'true') return;

    if (resource?.id != null && typeof resource.price === 'number') {
      const requestId = this.#nextPricingRequestId();
      const expectedVariantId = String(resource.id);
      this.dataset.pricingRequestId = String(requestId);

      const [cartAttributes, catalog] = await Promise.all([
        fetchCartAttributes(),
        Promise.resolve(loadStoreCatalog()),
      ]);

      applyStorePricingFromVariantResource(this, resource, cartAttributes, catalog, {
        requestId,
        expectedVariantId,
      });
      return;
    }

    if (variantId) {
      this.#refreshStorePricing(String(variantId));
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
